import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Header from "../Header";
import "./ResetPassword.css";

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasNumberOrSymbol: false,
    });
    const [isPasswordValid, setIsPasswordValid] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;

    const validatePassword = (password) => {
        const minLength = password.length >= 8;
        const hasNumberOrSymbol = /[0-9!@#$%^&*]/.test(password);

        setPasswordValidation({
            minLength,
            hasNumberOrSymbol,
        });

        setIsPasswordValid(minLength && hasNumberOrSymbol);
    };

    const handleResetPassword = async (event) => {
        event.preventDefault();
        setMessage("");
        setLoading(true);

        if (!newPassword || !confirmPassword) {
            setMessage("Both password fields are required.");
            setLoading(false);
            return;
        }

        if (!isPasswordValid) {
            setMessage("Password does not meet the requirements.");
            setLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setMessage("Passwords do not match.");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch("https://api.hgcdental.clinic/reset-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, newPassword, confirmPassword }),
            });

            const data = await response.json();
            setMessage(data.message);

            if (response.ok) {
                navigate("/login");
            }
        } catch (error) {
            setMessage("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="signup-container">
            <Header />
            <div className="signup-content">
                <div className="signup-card">
                    <h1 className="signup-title">Reset Password</h1>
                    <p className="signup-description">Enter your new password below.</p>

                    <form onSubmit={handleResetPassword} className="reset-password-form">
                        <div className="signup-form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <div className="password-container">
                                <input
                                    type={showNewPassword ? "text" : "password"}
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        validatePassword(e.target.value);
                                    }}
                                    onFocus={() => setShowValidation(true)}
                                    onBlur={() => setShowValidation(false)}
                                    placeholder="Enter new password"
                                    className="password-input"
                                    required
                                />
                                <span 
                                    className="eye-icon" 
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>
                            {showValidation && (
                                <div className="password-validation">
                                    <ul>
                                        <li className={passwordValidation.minLength ? "valid" : "invalid"}>
                                            At least 8 characters
                                        </li>
                                        <li className={passwordValidation.hasNumberOrSymbol ? "valid" : "invalid"}>
                                            Contains a number or symbol
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        <div className="signup-form-group">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="password-container">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    className="password-input"
                                    required
                                />
                                <span 
                                    className="eye-icon" 
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>
                        </div>

                        <div className="button-group">
                            <button 
                                type="submit" 
                                className="reset-password-button" 
                                disabled={loading || !isPasswordValid}
                            >
                                {loading ? "Resetting Password..." : "Reset Password"}
                            </button>
                            <button 
                                type="button" 
                                className="cancel-button" 
                                onClick={() => navigate("/login")}
                            >
                                Cancel
                            </button>
                        </div>

                        {message && <p className="reset-password-message">{message}</p>}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
